import { createContext } from "react";
import { API_BASE } from "../config";
import type { BaseMaxChars, Currency, Cutout, Language } from "../types";

/** A Christmas base as one sheet or one piece has it so far: whether it is
 *  switched on, and the message as they typed it (cleaned only when it is
 *  counted and sent, so the field never jumps under their thumb). Kept by the
 *  page for the visit, like how many (PageInfo.qtys), and never on the
 *  device. */
export interface BaseChoice {
  on: boolean;
  message: string;
}

/** What every order button on the page needs to know about the page itself.
 *
 *  There are up to three of them (the bar, the one after "Love it", one per
 *  piece), and they sit inside cards that otherwise know nothing about tokens
 *  or the studio's own look. Passing these down through each of those cards
 *  would make every card's props about the button rather than the card. */
export interface PageInfo {
  token: string;
  /** The studio's "See what they see": the button still opens, but nothing is
   *  sent, recorded or mailed on the client's behalf. */
  admin: boolean;
  language: Language;
  /** What the prices on the page are in, as the server said when it was read,
   *  or EUR from a server that said nothing. Never worked out here and never
   *  chosen here: the checkout sends it back only so the server can refuse a
   *  page that is out of date, and charges whatever the lead says. */
  currency: Currency;
  /** The lead's code, or "" when it was sent with none: see hasCode(). */
  promoCode: string;
  /** The name the lead was made with ("" when there is none), which the
   *  express box opens its name field with. */
  name?: string;
  /** The arrival window the thank-you showed when the payment was express,
   *  which the page says in place of the standard one until it is read again
   *  (the server then says it from the day it was paid). Absent otherwise. */
  paidArrives?: string;
  /** Whether "Pay here" is offered beside Etsy: to the customers the server
   *  says (CHECKOUT_AUDIENCE), and to a signed-in admin in the studio's look.
   *  Never without a publishable key in this build, whatever the server said. */
  checkout: boolean;
  /** Paid for already, or as good as: the server says so (and nothing was
   *  refunded), or that a payment is still on its way from the bank; a payment
   *  went through in the sheet on this visit; or they have come back from
   *  Stripe and the page is still asking what happened, or could not find out.
   *  The customer's order buttons step aside while it is true, because the only
   *  thing left for them to do is pay a second time. The studio's look keeps
   *  its own, so the owner can still try the checkout on a page that has sold. */
  ordered: boolean;
  /** The sheet's checkout finished, said to the page as well as in the sheet:
   *  "checking" the moment Stripe says so, then what the server made of it.
   *  The page stops offering the button that opened the sheet at once, so
   *  closing the sheet mid-check does not bring it back, and puts the session
   *  in the address, so a reload asks about it as a return from a bank does. */
  onPaid: (result: "checking" | "paid" | "processing", session: string, arrives?: string) => void;
  /** Read the page again, quietly: the checkout said the prices on screen, or
   *  the piece, are no longer the ones it charges, that this is already paid
   *  for or being paid, or that it will not open one for this page again. A
   *  failed read leaves the page as it is. */
  refresh: () => void;
  /** Read again what the page knows of their other previews (the strip, and
   *  what "Order them together" can take of them): the checkout said one of
   *  them is not what the page showed any more. */
  refreshOthers?: () => void;
  /** The size they have picked, on this visit or on an earlier one on this
   *  device (preview/remember.ts): the ladder draws it pressed, the order
   *  button names it with its price, and the sheet opens on it. Null until
   *  they pick one. Only for a page of one piece: three pieces have three
   *  sizes, each kept by its own sheet. */
  size?: string | null;
  /** They picked a size, anywhere on the page: remembered on the device and
   *  told to every button at once. */
  onSize?: (size: string) => void;
  /** How far they got in the order sheet on this device, this visit or an
   *  earlier one: "pay" once they asked for the payment form. The button
   *  then reads "Continue to payment · 4 cm · €X" and opens on the form. */
  step?: "pick" | "pay" | null;
  /** They reached a step of the sheet. */
  onStep?: (step: "pick" | "pay") => void;
  /** How many they chose in an order sheet on this visit, where the page
   *  decides how many (the multi-buy, preview/Quantity.tsx), by qtyKey():
   *  the page's own piece, whichever of its buttons opened the sheet, or one
   *  piece of several. The sheet is gone the moment it is closed, and with it
   *  whatever it held: someone who chose two, went on to the form, closed it
   *  and came back by the button that offers to continue was handed a form
   *  for one, under a button that had said the price of one. Kept here, so
   *  every button names the number and the total and every sheet opens on
   *  it. Never on the device (preview/remember.ts): a later visit starts at
   *  one, as it always did. */
  qtys?: Record<string, number>;
  /** They changed how many in a sheet. */
  onQty?: (key: string, qty: number) => void;
  /** The Christmas base, where the page offers it (the server's
   *  christmas.base: in its window, on a page offered "Pay here"): how many
   *  characters its message may be per product and size. Null or absent: no
   *  base anywhere on the page, and nothing about it is sent. */
  baseLimits?: BaseMaxChars | null;
  /** The base chosen in each sheet or on each piece, under the same keys as
   *  `qtys` (qtyKey() for a sheet of one piece, qtySel() for a piece in the
   *  order), so closing the sheet and coming back by a button that offers to
   *  continue opens on the base and the words they had, and the form is made
   *  for them. Absent for none chosen. */
  bases?: Record<string, BaseChoice>;
  /** They switched a base on or off, or typed in its message. */
  onBase?: (key: string, choice: BaseChoice) => void;
  /** Each statue drawing's figure alone (cut out of it when it was drawn), by
   *  the drawing's url, which BaseScene stands on the real base. Drawings
   *  made before have none, and their base is shown on its own. */
  cutouts?: Record<string, Cutout>;
  /** On a page of several that lets them choose, which pieces are in their
   *  order and at which size: a size per piece key (selection.ts), or null
   *  for one left out. Held for the visit and never on the device. */
  picks?: Record<string, string | null>;
  /** They put a piece in the order at a size, or took it out (null). */
  onPick?: (key: string, size: string | null) => void;
  /** How many times this device has opened the page, this visit included:
   *  1 on a first visit. 0 when the device will not say (private browsing,
   *  the studio's own look). */
  visits?: number;
}

export const PageContext = createContext<PageInfo | null>(null);

/** What became of one checkout session, as the server hears it from Stripe
 *  and from its own record of the order.
 *
 *  "paid": the money is in, and none of it has been given back in full.
 *  "processing": the checkout finished but the bank has not paid yet, which
 *  some methods take days over. "failed": the session is still open, or
 *  expired, which is what a payment cancelled or refused at the bank comes
 *  back as, or the bank's payment failed after the checkout finished; or it is
 *  not this page's at all. "refunded": it was paid and has since been given
 *  back in full, which is nothing to tell them about on the page any more.
 *  "unknown": the server could not be asked, three times over, and so nothing
 *  is known either way. What each of those says to the customer is up to
 *  where it is asked from.
 *
 *  Nothing else comes back: no email, no address. The session id in a return
 *  URL is not a secret, and the token already names the page. */
export type CheckoutResult = "paid" | "processing" | "failed" | "refunded" | "unknown";

export async function checkoutResult(token: string, session: string): Promise<CheckoutResult> {
  const url = `${API_BASE}/api/checkout?${new URLSearchParams({ session, token })}`;
  for (let attempt = 0; ; attempt++) {
    try {
      const r = await fetch(url, { cache: "no-store" });
      if (r.status === 404) return "failed";
      if (r.ok) {
        const data = (await r.json()) as { status?: string; paid?: boolean; outcome?: string };
        switch (data.outcome) {
          case "paid":
          case "processing":
          case "refunded":
          case "failed":
            return data.outcome;
          // Still open: they left the form or their bank's app without paying.
          case "open":
            return "failed";
        }
        // A server from before it said the outcome itself.
        if (data.paid) return "paid";
        return data.status === "complete" ? "processing" : "failed";
      }
      // Only a server having a bad moment is worth asking again; any other
      // refusal will be the same refusal a second later.
      if (r.status < 500) return "unknown";
    } catch {
      // Asked again below. Back from a bank's app on a phone, the first
      // request is the one most likely to fail.
    }
    if (attempt >= 2) return "unknown";
    await new Promise((resolve) => window.setTimeout(resolve, 1500 * (attempt + 1)));
  }
}
